import type { PromocionVista, ProductoVista, PaqueteVista, DiasSemana } from '@shared/types';
import { horarioEnPalabras } from '@shared/ventanaDisponibilidad';

/**
 * Sprint 16 — Helpers puros para presentar Promociones en lenguaje natural
 * y derivar su estado real (no sólo `activa` técnica, sino aplica-AHORA o no).
 */

export type EstadoPromo =
  | 'activa_hoy'      // activa + en vigencia + día de la semana aplica
  | 'fuera_horario'   // activa + en vigencia pero hoy NO aplica
  | 'vence_pronto'    // activa_hoy + vigenciaFin <= 7 días
  | 'programada'      // vigenciaInicio > hoy
  | 'vencida'         // vigenciaFin < hoy (sin importar `activa`)
  | 'pausada';        // activa=false manual

const DIAS_KEYS: (keyof DiasSemana)[] = [
  'domingo', 'lunes', 'martes', 'miercoles', 'jueves', 'viernes', 'sabado',
];
const DIAS_LABELS = ['domingo', 'lunes', 'martes', 'miércoles', 'jueves', 'viernes', 'sábado'];

export function estadoPromocion(p: PromocionVista, hoy = new Date()): EstadoPromo {
  const ini = new Date(p.vigenciaInicio);
  const fin = new Date(p.vigenciaFin);
  // Normalizar al fin del día para que el día de vencimiento todavía cuente como vigente.
  fin.setHours(23, 59, 59, 999);

  if (!p.activa) return 'pausada';
  if (fin < hoy) return 'vencida';
  if (ini > hoy) return 'programada';

  // En vigencia. Validar día de la semana.
  if (p.diasSemana) {
    const todosSelectos = DIAS_KEYS.every((k) => p.diasSemana![k]);
    if (!todosSelectos) {
      const hoyKey = DIAS_KEYS[hoy.getDay()];
      if (!p.diasSemana[hoyKey]) return 'fuera_horario';
    }
  }

  // Activa hoy. ¿Vence pronto?
  const diasParaVencer = Math.ceil((fin.getTime() - hoy.getTime()) / (1000 * 60 * 60 * 24));
  if (diasParaVencer <= 7) return 'vence_pronto';
  return 'activa_hoy';
}

export function diasParaVencer(p: PromocionVista, hoy = new Date()): number {
  const fin = new Date(p.vigenciaFin);
  fin.setHours(23, 59, 59, 999);
  return Math.ceil((fin.getTime() - hoy.getTime()) / (1000 * 60 * 60 * 24));
}

export function diasParaIniciar(p: PromocionVista, hoy = new Date()): number {
  const ini = new Date(p.vigenciaInicio);
  return Math.ceil((ini.getTime() - hoy.getTime()) / (1000 * 60 * 60 * 24));
}

/**
 * Sprint 74 — Cuándo aplica, con horas: "Sólo miércoles a viernes · 1:00 p.m. a 5:00 p.m.".
 *
 * La tarjeta mostraba sólo los días. Una promoción con horario que no lo enseña en la lista no se
 * puede auditar de un vistazo, y el horario es precisamente el dato que decide si se está regalando
 * dinero fuera de hora.
 */
export function cuandoAplicaLegible(p: {
  diasSemana?: DiasSemana | null;
  horaInicio?: number | null;
  horaFin?: number | null;
}): string {
  const dias = diasSemanaLegibles(p.diasSemana);
  const horas = horarioEnPalabras({
    diasSemana: null,
    horaInicio: p.horaInicio ?? null,
    horaFin: p.horaFin ?? null,
  });
  if (!horas) return dias;
  // Con horario, "Todos los días" sobra: lo que importa es la franja.
  return dias === 'Todos los días' ? horas : `${dias} · ${horas}`;
}

/**
 * Frase natural: "Sólo lunes y martes" / "Todos los días" / "Sólo viernes".
 */
export function diasSemanaLegibles(ds?: DiasSemana | null): string {
  if (!ds) return 'Todos los días';
  const seleccionados: string[] = [];
  for (let i = 0; i < 7; i++) {
    if (ds[DIAS_KEYS[i]]) seleccionados.push(DIAS_LABELS[i]);
  }
  if (seleccionados.length === 7) return 'Todos los días';
  if (seleccionados.length === 0) return 'Ningún día (configura los días aplicables)';
  if (seleccionados.length === 1) return `Sólo ${seleccionados[0]}`;
  if (seleccionados.length === 2) return `Sólo ${seleccionados[0]} y ${seleccionados[1]}`;
  return `Sólo ${seleccionados.slice(0, -1).join(', ')} y ${seleccionados[seleccionados.length - 1]}`;
}

/**
 * Frase principal de la promo en lenguaje natural.
 * Ej: "20% de descuento" / "$50 de descuento" / "Precio fijo $100" / "Llévate 1 × Coca-Cola gratis"
 */
export function tituloPromocion(
  p: PromocionVista,
  productos: ProductoVista[],
  moneda: string,
  locale: string,
): string {
  // Sprint 39 — `n` viene en centavos; convertir a pesos antes de formatear.
  const fmt = (centavos: number) => new Intl.NumberFormat(locale, { style: 'currency', currency: 'MXN' })
    .format(centavos / 100)
    .replace('MX$', moneda)
    .replace('$', moneda);
  switch (p.tipo) {
    case 'DESCUENTO_PORCENTAJE': {
      const tope = p.topeMonto ? ` (máx ${fmt(p.topeMonto)})` : '';
      return `${p.valor ?? 0}% de descuento${tope}`;
    }
    case 'DESCUENTO_MONTO':
      return `${fmt(p.valor ?? 0)} de descuento`;
    case 'PRECIO_FIJO':
      return `Precio fijo ${fmt(p.valor ?? 0)}`;
    // Sprint 74 — "2 x $15": el precio es del grupo, no de la pieza. Decirlo como el cartel.
    case 'N_POR_PRECIO':
      return `${p.cantidadPaquete ?? 2} x ${fmt(p.valor ?? 0)}`;
    case 'REGALO': {
      const prod = productos.find((x) => x.id === p.productoRegaloId);
      const n = p.cantidadRegalo ?? 1;
      const sufijo = p.regaloDescuentaDelTotal
        ? ` (se descuenta ${fmt((prod?.precio ?? 0) * n)} del total)`
        : ' (cortesía — no afecta el total)';
      return `Llévate ${n} × ${prod?.nombre ?? 'un producto'} GRATIS${sufijo}`;
    }
  }
}

/**
 * Devuelve los nombres de los elementos elegibles (productos y paquetes).
 * Truncados con "+ N más" si pasan de N visibles.
 */
export function elegiblesLegibles(
  p: PromocionVista,
  productos: ProductoVista[],
  paquetes: PaqueteVista[],
  maxVisibles = 4,
): { items: string[]; resto: number } {
  const items: string[] = [];
  for (const id of p.productosElegiblesIds) {
    const prod = productos.find((x) => x.id === id);
    if (prod) items.push(prod.nombre);
  }
  for (const id of p.paquetesElegiblesIds) {
    const pq = paquetes.find((x) => x.id === id);
    if (pq) items.push(`📦 ${pq.nombre}`);
  }
  const resto = Math.max(0, items.length - maxVisibles);
  return { items: items.slice(0, maxVisibles), resto };
}
